import React, { useState, lazy } from 'react';
import { Layout } from '../components/layout/Layout';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Button } from '../components/ui/Button';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Car, Shirt, Clock, Phone, MessageCircle, Youtube, Facebook, Heart, ArrowRight, Navigation, Mail, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
const faqs = [{
  icon: Navigation,
  question: 'How do I get there?',
  answer: 'Just add our address to any GPS system and in no time, you will be here. See you soon.'
}, {
  icon: Car,
  question: 'Where should I park?',
  answer: 'On our campus, parking is not a problem at all, and it is free parking. Come and see.'
}, {
  icon: Shirt,
  question: 'What should I wear?',
  answer: 'Our church has a "come as you are" atmosphere. You will see some dressed up dapper and many dressed casual. As long as your attire is decent and you are comfortable in what you are wearing, that\'s all that matters. In our Church, we have no peculiar rules for anyone as regards dressing; decency is the key.'
}];
export function PlanYourVisitPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <Layout data-id="element-1233">
      {/* Hero Section */}
      <div className="relative bg-[#0A1F44] pt-44 md:pt-48 pb-20 text-white overflow-hidden" data-id="element-1234">
        <div className="absolute inset-0 opacity-20" data-id="element-1235">
          <img src="https://images.unsplash.com/photo-1438232992991-995b7058bbb3?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" alt="Background" className="w-full h-full object-cover" data-id="element-1236" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F44]/80 to-[#0A1F44]" data-id="element-1237" />

        <div className="container-custom relative z-10" data-id="element-1238">
          <div className="max-w-3xl" data-id="element-1239">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-block text-[#fc0719] text-sm font-semibold tracking-widest uppercase mb-4" data-id="element-1240">
              Welcome Home
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-6xl font-serif font-bold mb-6" data-id="element-1241">
              Plan Your Visit
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-xl text-white/80 leading-relaxed max-w-2xl" data-id="element-1242">
              We can't wait to meet you! Here's everything you need to know
              before you arrive at TheHousehold.
            </motion.p>
          </div>
        </div>
      </div>

      {/* Welcome Message */}
      <section className="py-20 bg-white" data-id="element-1243">
        <div className="container-custom" data-id="element-1244">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-1245">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-1246">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#0A1F44] mb-6" data-id="element-1247">
                A Place for You
              </h2>
              <div className="space-y-4" data-id="element-1248">
                <p className="text-gray-600 leading-relaxed" data-id="element-1249">
                  At TheHousehold, you'll find a diverse community of believers
                  who are passionate about Jesus. Our services are designed to
                  help you connect with God through powerful worship and
                  practical, biblical teaching.
                </p>
                <p className="text-gray-600 leading-relaxed italic" data-id="element-1250">
                  "For my house shall be called a house of prayer for all
                  nations. I will make them joyful in my house of prayer. I will
                  accept their burnt offerings and sacrifices on my altar, for I
                  am the Lord of TheHousehold."
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="relative" data-id="element-1251">
              <div className="bg-[#F5F7FA] rounded-2xl p-8 md:p-10" data-id="element-1252">
                <Heart className="w-12 h-12 text-[#fc0719] mb-6" data-id="element-1253" />
                <p className="text-xl font-serif text-[#0A1F44] leading-relaxed italic" data-id="element-1254">
                  "No matter where you are now in your walk with God, we believe
                  that it can be sweeter and better, if you wholeheartedly
                  embrace the grace and mercy of God, through Jesus Christ, The
                  Lord of TheHousehold."
                </p>
                <p className="mt-6 text-[#0A1F44] font-semibold" data-id="element-1255">— Shalom</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20 bg-[#0A1F44] text-white" data-id="element-1256">
        <div className="container-custom" data-id="element-1257">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-3xl" data-id="element-1258">
            <span className="inline-block text-[#fc0719] text-sm font-semibold tracking-widest uppercase mb-4" data-id="element-1259">
              Join Our Family
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6" data-id="element-1260">
              What to Expect
            </h2>
            <p className="text-white/80 text-lg mb-12" data-id="element-1261">
              Our services are designed to help you encounter God and connect
              with others.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-1262">
            <div className="bg-white p-8 rounded-2xl shadow-md text-center" data-id="element-1263">
              <Clock className="w-10 h-10 text-[#fc0719] mx-auto mb-4" data-id="element-1264" />
              <h3 className="text-xl font-bold text-[#0A1F44] mb-2" data-id="element-1265">
                Sunday Service
              </h3>
              <p className="text-white/80" data-id="element-1266">10:00 AM EST</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-md text-center" data-id="element-1267">
              <Clock className="w-10 h-10 text-[#fc0719] mx-auto mb-4" data-id="element-1268" />
              <h3 className="text-xl font-bold text-[#0A1F44] mb-2" data-id="element-1269">
                Mid-Week Study
              </h3>
              <p className="text-white/80" data-id="element-1270">7:00 PM EST</p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-md text-center" data-id="element-1271">
              <Clock className="w-10 h-10 text-[#fc0719] mx-auto mb-4" data-id="element-1272" />
              <h3 className="text-xl font-bold text-[#0A1F44] mb-2" data-id="element-1273">
                Youth Meeting
              </h3>
              <p className="text-white/80" data-id="element-1274">Sunday 9:00 AM EST</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-[#F5F7FA]" data-id="element-1275">
        <div className="container-custom" data-id="element-1276">
          <SectionHeading title="Common Questions" subtitle="FAQ" data-id="element-1277" />

          <div className="max-w-3xl mx-auto space-y-4" data-id="element-1278">
            {faqs.map((faq, index) => <motion.div key={faq.question} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100" data-id="element-1279">
                <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full p-6 md:p-8 text-left flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors" data-id="element-1280">
                  <div className="flex items-center gap-6" data-id="element-1281">
                    <div className="w-12 h-12 bg-[#0A1F44] rounded-full flex items-center justify-center shrink-0" data-id="element-1282">
                      <faq.icon className="w-6 h-6 text-white" data-id="element-1283" />
                    </div>
                    <h3 className="text-lg md:text-xl font-bold text-[#0A1F44]" data-id="element-1284">
                      {faq.question}
                    </h3>
                  </div>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${openIndex === index ? 'bg-[#fc0719] rotate-180' : 'bg-gray-100'}`} data-id="element-1285">
                    <ChevronDown className={`w-5 h-5 ${openIndex === index ? 'text-white' : 'text-[#0A1F44]'}`} data-id="element-1286" />
                  </div>
                </button>

                <AnimatePresence data-id="element-1287">
                  {openIndex === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} data-id="element-1288">
                      <div className="px-6 md:px-8 pb-8 md:pb-10 md:ml-[72px]" data-id="element-1289">
                        <div className="h-[1px] w-full bg-gray-100 mb-6" data-id="element-1290" />
                        <p className="text-gray-600 leading-relaxed text-lg" data-id="element-1291">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Location & Contact */}
      <section className="py-20 bg-white" data-id="element-1292">
        <div className="container-custom" data-id="element-1293">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-1294">
            {/* Map/Location */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-gray-100 rounded-2xl overflow-hidden min-h-[400px] relative" data-id="element-1295">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3106.84123456789!2d-76.85!3d38.9!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzjCsDU0JzAwLjAiTiA3NsKwNTEnMDAuMCJX!5e0!3m2!1sen!2sus!4v1234567890" width="100%" height="100%" style={{
              border: 0
            }} allowFullScreen loading="lazy" title="TheHousehold Church Location" data-id="element-1296"></iframe>
              <div className="absolute bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:bottom-4 z-10" data-id="element-1297">
                <a href="https://maps.google.com/?q=1221+Caraway+Ct+%231090+Largo+MD+20774" target="_blank" rel="noopener noreferrer" data-id="element-1298">
                  <Button variant="primary" className="shadow-lg" data-id="element-1299">
                    <MapPin className="w-4 h-4 mr-2" data-id="element-1300" /> Get Directions
                  </Button>
                </a>
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-1301">
              <span className="text-[#fc0719] text-sm font-semibold tracking-widest uppercase mb-4 block" data-id="element-1302">
                Get In Touch
              </span>
              <h2 className="text-3xl font-serif font-bold text-[#0A1F44] mb-6" data-id="element-1303">
                We'd Love to Hear From You
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed" data-id="element-1304">
                After your tour of our website, do not hesitate to leave a
                comment or you can also call or send us a text. We look forward
                to reading from you.
              </p>

              <div className="space-y-4 mb-8" data-id="element-1305">
                <a href="tel:301-996-6907" className="flex items-center gap-4 p-4 bg-[#F5F7FA] rounded-xl hover:bg-[#e2e8f0] transition-colors" data-id="element-1306">
                  <div className="w-12 h-12 bg-[#0A1F44] rounded-full flex items-center justify-center" data-id="element-1307">
                    <Phone className="w-5 h-5 text-white" data-id="element-1308" />
                  </div>
                  <div data-id="element-1309">
                    <p className="text-sm text-gray-500" data-id="element-1310">Call or Text</p>
                    <div className="flex flex-col" data-id="element-1311">
                      <p className="text-[#0A1F44] font-semibold" data-id="element-1312">
                        (301) 996-6907
                      </p>
                      <p className="text-[#0A1F44] font-semibold" data-id="element-1313">
                        +1 (832) 447-7348
                      </p>
                    </div>
                  </div>
                </a>

                <a href="mailto:Info@householdchurch.org" className="flex items-center gap-4 p-4 bg-[#F5F7FA] rounded-xl hover:bg-[#e2e8f0] transition-colors" data-id="element-1314">
                  <div className="w-12 h-12 bg-[#fc0719] rounded-full flex items-center justify-center" data-id="element-1315">
                    <Mail className="w-5 h-5 text-white" data-id="element-1316" />
                  </div>
                  <div data-id="element-1317">
                    <p className="text-sm text-gray-500" data-id="element-1318">Email Us</p>
                    <p className="text-[#0A1F44] font-semibold" data-id="element-1319">
                      Info@householdchurch.org
                    </p>
                  </div>
                </a>

                <Link to="/contact" className="flex items-center gap-4 p-4 bg-[#F5F7FA] rounded-xl hover:bg-[#e2e8f0] transition-colors" data-id="element-1320">
                  <div className="w-12 h-12 bg-[#0A1F44] rounded-full flex items-center justify-center" data-id="element-1321">
                    <MessageCircle className="w-5 h-5 text-white" data-id="element-1322" />
                  </div>
                  <div data-id="element-1323">
                    <p className="text-sm text-gray-500" data-id="element-1324">Send a Message</p>
                    <p className="text-[#0A1F44] font-semibold" data-id="element-1325">Contact Form</p>
                  </div>
                </Link>
              </div>

              {/* Can't Visit In Person */}
              <div className="bg-[#0A1F44] p-6 rounded-2xl" data-id="element-1326">
                <h3 className="text-white font-serif font-bold mb-3" data-id="element-1327">
                  Can't visit in person?
                </h3>
                <p className="text-gray-400 text-sm mb-4" data-id="element-1328">
                  Join us online via our livestream platforms
                </p>
                <div className="flex gap-3" data-id="element-1329">
                  <a href="https://youtube.com/@householdchurch" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-colors text-sm" data-id="element-1330">
                    <Youtube className="w-4 h-4" data-id="element-1331" />
                    YouTube
                  </a>
                  <a href="https://facebook.com/householdchurch" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-colors text-sm" data-id="element-1332">
                    <Facebook className="w-4 h-4" data-id="element-1333" />
                    Facebook
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>;
}